import React, { useMemo } from "react";
import { Play } from "lucide-react";
import { Button } from "../ui/button";
import { getSlotRecommendation } from "../../config/periodisation";
import {
  builtName,
  slotDate,
  slotName,
  slotPosition,
} from "../../utils/programmeSchedule";

/**
 * The programme's next session, at the top of the tab: the thing you come
 * here to do. Start runs it if it's built, or builds it first. Only states
 * what the programme holds: its place in the week and what the slot asks for.
 */
const ProgrammeNextCard = ({ programme, slot, onStart, onView, onChange, mateBuilder = null }) => {
  const rec = useMemo(
    () => getSlotRecommendation(programme, slot),
    [programme, slot],
  );
  if (!slot) return null;

  const { index, count } = slotPosition(programme, slot);
  const built = Boolean(slot.plan_data);
  const name = builtName(slot);
  const facts = [
    rec?.durationMin && `${rec.durationMin} min`,
    rec?.reps && `${rec.reps} reps`,
    rec?.rest && (/\d/.test(rec.rest) ? `${rec.rest} rest` : rec.rest),
  ].filter(Boolean);
  // The day it's planned for (#92), when the programme maps days to dates.
  const date = slotDate(programme, slot);
  const day = date
    ? date.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short" })
    : null;
  const place = day
    ? `Planned for ${day}`
    : index
      ? `Session ${index} of ${count} this week`
      : "Up next";
  const reason = built
    ? `${place}, built and ready to run.`
    : mateBuilder
      ? `${place}. ${mateBuilder} built it: Start runs theirs.`
      : `${place}. Start builds it from your programme first, then opens it.`;

  return (
    <section
      aria-label="Next session"
      className="grid gap-2.5 rounded-2xl border border-primary/40 bg-primary/[0.07] p-4"
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primary">
        Next · week {slot.week_number}
        {index ? `, session ${index} of ${count}` : ""}
      </p>
      <div>
        <h3 className="text-xl font-bold leading-tight text-white [text-wrap:balance]">
          {slotName(slot)}
        </h3>
        {name && <p className="mt-0.5 text-sm text-foreground">{name}</p>}
      </div>
      {facts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {facts.map((fact) => (
            <span
              key={fact}
              className="rounded-full border border-border bg-white/5 px-2.5 py-0.5 text-xs text-foreground"
            >
              {fact}
            </span>
          ))}
        </div>
      )}
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        {rec?.isDeload ? `${reason} Deload week: lighter on purpose.` : reason}
      </p>
      <div className="mt-0.5 flex gap-2">
        <Button
          type="button"
          onClick={() => onStart?.(slot)}
          className="h-12 flex-1 rounded-xl text-[15px] font-bold"
        >
          <Play className="mr-2 h-4 w-4 fill-current" />
          Start
        </Button>
        {/* Do another of this week's sessions today, or something else (#196). */}
        {onChange && (
          <Button
            type="button"
            variant="outline"
            onClick={onChange}
            className="h-12 rounded-xl border-border bg-muted/40 px-4 font-semibold hover:bg-muted/60"
          >
            Change
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => onView?.(slot)}
          className="h-12 rounded-xl border-border bg-muted/40 px-5 font-semibold hover:bg-muted/60"
        >
          {built ? "View" : "Details"}
        </Button>
      </div>
    </section>
  );
};

export default ProgrammeNextCard;
